import React, { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { Package } from '../../services/packages.service'
import { QuickAssessmentData } from '../../services/smart-matching.service'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { PhoneInput } from '../ui'
import { isValidPhone } from '../../constants/phone'

// Simplified validation schema - 5 core fields required
const deceasedInfoSchema = z.object({
  // Core required fields
  deceasedName: z.string().min(2, 'Deceased name is required'),
  deceasedGender: z.enum(['MALE', 'FEMALE'], { required_error: 'Gender is required' }),
  primaryContact: z.string().min(2, 'Primary contact name is required'),
  primaryContactPhone: z
    .string()
    .min(1, 'Primary contact phone is required')
    .refine(isValidPhone, 'Enter a valid Malaysian or Singaporean phone number'),
  preferredBurialDate: z.string().min(1, 'Preferred burial date is required'),

  // Optional fields (relationship and hidden behind progressive disclosure)
  relationshipToDeceased: z.string().min(2, 'Relationship to deceased is required').optional(),
  deceasedAge: z.number().int().positive().optional(),
  deceasedIC: z.string().optional(),
  placeOfDeath: z.string().optional(),
  timeOfDeath: z.string().optional(),
  causeOfDeath: z.string().optional(),
  secondaryContact: z.string().optional(),
  secondaryContactPhone: z
    .string()
    .optional()
    .refine((v) => !v || isValidPhone(v), 'Enter a valid Malaysian or Singaporean phone number'),
  preferredBurialTime: z.string().optional(),
  mosquePreference: z.string().optional(),
  pickupAddress: z.string().optional(),
  burialCemetery: z.string().optional(),
  burialPlot: z.string().optional(),
  notes: z.string().optional(),
})

type DeceasedInfoFormData = z.infer<typeof deceasedInfoSchema>

interface DeceasedInformationStepProps {
  selectedPackage: Package
  assessmentData: QuickAssessmentData
  onNext: (caseData: any) => void
  onBack: () => void
}

function DeceasedInformationStep({
  selectedPackage,
  assessmentData,
  onNext,
  onBack
}: DeceasedInformationStepProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Collapsible sections state
  const [showDeceasedDetails, setShowDeceasedDetails] = useState(false)
  const [showSecondaryContact, setShowSecondaryContact] = useState(false)
  const [showBurialDetails, setShowBurialDetails] = useState(false)

  // Smart defaults based on assessment urgency
  const getDefaultBurialDate = () => {
    const today = new Date()
    switch (assessmentData.urgencyLevel) {
      case 'IMMEDIATE':
        return today.toISOString().split('T')[0]
      case 'TODAY':
        return today.toISOString().split('T')[0]
      case 'TOMORROW':
        const tomorrow = new Date(today)
        tomorrow.setDate(today.getDate() + 1)
        return tomorrow.toISOString().split('T')[0]
      default:
        return today.toISOString().split('T')[0]
    }
  }

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<DeceasedInfoFormData>({
    resolver: zodResolver(deceasedInfoSchema),
    defaultValues: {
      pickupAddress: assessmentData.currentLocation,
      burialCemetery: assessmentData.preferredBurialArea,
      preferredBurialDate: getDefaultBurialDate(),
    },
    mode: 'onChange',
  })

  const onSubmit = async (data: DeceasedInfoFormData) => {
    try {
      setIsSubmitting(true)

      // Prepare deceased data structure (no case creation yet)
      const deceasedData = {
        ...data,
        deceasedAge: data.deceasedAge ? Number(data.deceasedAge) : undefined,
        // Map relationship to notes for compatibility with existing schema
        ...(data.relationshipToDeceased && {
          notes: data.notes
            ? `Relationship: ${data.relationshipToDeceased}. ${data.notes}`
            : `Relationship: ${data.relationshipToDeceased}`
        }),
      }

      // Pass the deceased data directly to the next step without creating a case
      onNext(deceasedData)
    } catch (error) {
      console.error('Error processing deceased information:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
          <span className="text-2xl">📋</span>
        </div>
        <h1 className="text-3xl font-bold text-neutral-900">
          Deceased Information
        </h1>
        <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
          Please provide the essential information about the deceased and contact details.
          This information is required for us to arrange the funeral services properly.
        </p>
      </div>

      {/* Progress Indicator */}
      <div className="flex items-center justify-center space-x-2">
        <div className="flex items-center">
          <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
            ✓
          </div>
          <span className="ml-2 text-sm font-medium text-green-600">Quick Assessment</span>
        </div>
        <div className="w-8 h-0.5 bg-green-600"></div>
        <div className="flex items-center">
          <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
            ✓
          </div>
          <span className="ml-2 text-sm font-medium text-green-600">Package Selection</span>
        </div>
        <div className="w-8 h-0.5 bg-green-600"></div>
        <div className="flex items-center">
          <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium">
            3
          </div>
          <span className="ml-2 text-sm font-medium text-primary-600">Deceased Information</span>
        </div>
        <div className="w-8 h-0.5 bg-neutral-300"></div>
        <div className="flex items-center">
          <div className="w-8 h-8 bg-neutral-300 text-neutral-500 rounded-full flex items-center justify-center text-sm font-medium">
            4
          </div>
          <span className="ml-2 text-sm text-neutral-500">Provider Matches</span>
        </div>
      </div>

      {/* Selected Package Summary */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-neutral-900 mb-4">Selected Package</h2>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-lg text-neutral-900">{selectedPackage.name}</h3>
              <p className="text-neutral-600">{selectedPackage.description}</p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-primary-600">
                RM {selectedPackage.price.toLocaleString()}
              </div>
              <div className="text-sm text-neutral-500">
                {selectedPackage.includes.length} services included
              </div>
            </div>
          </div>
        </div>
      </Card>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Essential Information - Always Visible */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 border-2 border-blue-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center mb-6">
            <div className="ml-4">
              <h2 className="text-xl font-bold text-neutral-900">Essential Information</h2>
              <p className="text-sm text-neutral-600">Required information to continue with the funeral arrangement</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Deceased Core Information */}
            <div>
              <h3 className="text-base font-semibold text-neutral-900 mb-4">
                Deceased Information
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="deceasedName" className="block text-sm font-medium text-neutral-700 mb-2">
                    Full Name <span className="text-red-500 font-semibold">*</span>
                  </label>
                  <input
                    id="deceasedName"
                    type="text"
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                      errors.deceasedName ? 'border-red-300 bg-red-50' : 'border-neutral-300'
                    }`}
                    {...register('deceasedName')}
                    placeholder="Enter full name"
                  />
                  {errors.deceasedName && (
                    <p className="mt-1 text-sm text-red-600 font-medium">{errors.deceasedName.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="deceasedGender" className="block text-sm font-medium text-neutral-700 mb-2">
                    Gender <span className="text-red-500 font-semibold">*</span>
                  </label>
                  <select
                    id="deceasedGender"
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                      errors.deceasedGender ? 'border-red-300 bg-red-50' : 'border-neutral-300'
                    }`}
                    {...register('deceasedGender')}
                  >
                    <option value="">Select gender</option>
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                  </select>
                  {errors.deceasedGender && (
                    <p className="mt-1 text-sm text-red-600 font-medium">{errors.deceasedGender.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Core Information */}
            <div>
              <h3 className="text-base font-semibold text-neutral-900 mb-4">
                Primary Contact
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label htmlFor="primaryContact" className="block text-sm font-medium text-neutral-700 mb-2">
                    Contact Name <span className="text-red-500 font-semibold">*</span>
                  </label>
                  <input
                    id="primaryContact"
                    type="text"
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                      errors.primaryContact ? 'border-red-300 bg-red-50' : 'border-neutral-300'
                    }`}
                    {...register('primaryContact')}
                    placeholder="Full name"
                  />
                  {errors.primaryContact && (
                    <p className="mt-1 text-sm text-red-600 font-medium">{errors.primaryContact.message}</p>
                  )}
                </div>

                <div className="mb-2">
                  <Controller
                    name="primaryContactPhone"
                    control={control}
                    render={({ field, fieldState }) => (
                      <PhoneInput
                        label="Phone Number"
                        required
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        error={fieldState.error?.message}
                      />
                    )}
                  />
                </div>

                <div>
                  <label htmlFor="relationshipToDeceased" className="block text-sm font-medium text-neutral-700 mb-2">
                    Relationship <span className="text-neutral-400 text-xs">(optional)</span>
                  </label>
                  <select
                    id="relationshipToDeceased"
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                      errors.relationshipToDeceased ? 'border-red-300 bg-red-50' : 'border-neutral-300'
                    }`}
                    {...register('relationshipToDeceased')}
                  >
                    <option value="">Select relationship</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Child">Child</option>
                    <option value="Parent">Parent</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Other Family">Other Family</option>
                    <option value="Friend">Friend</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.relationshipToDeceased && (
                    <p className="mt-1 text-sm text-red-600 font-medium">{errors.relationshipToDeceased.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Burial Core Information */}
            <div>
              <h3 className="text-base font-semibold text-neutral-900 mb-4">
                Burial Preferences
              </h3>
              <div className="max-w-md">
                <label htmlFor="preferredBurialDate" className="block text-sm font-medium text-neutral-700 mb-2">
                  Preferred Burial Date <span className="text-red-500 font-semibold">*</span>
                </label>
                <input
                  id="preferredBurialDate"
                  type="date"
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                    errors.preferredBurialDate ? 'border-red-300 bg-red-50' : 'border-neutral-300'
                  }`}
                  {...register('preferredBurialDate')}
                />
                {errors.preferredBurialDate && (
                  <p className="mt-1 text-sm text-red-600 font-medium">{errors.preferredBurialDate.message}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Optional Details - Collapsible Sections */}
        <div className="space-y-4">
          {/* Additional Deceased Details */}
          <div className="bg-white border border-neutral-200 rounded-lg shadow-sm">
            <button
              type="button"
              onClick={() => setShowDeceasedDetails(!showDeceasedDetails)}
              className="w-full px-6 py-4 text-left hover:bg-neutral-50 transition-colors rounded-lg flex items-center justify-between group"
            >
              <div className="flex items-center">
                {showDeceasedDetails ? (
                  <ChevronDownIcon className="w-5 h-5 text-neutral-500 mr-3 group-hover:text-neutral-700 transition-colors" />
                ) : (
                  <ChevronRightIcon className="w-5 h-5 text-neutral-500 mr-3 group-hover:text-neutral-700 transition-colors" />
                )}
                <div>
                  <h3 className="text-base font-semibold text-neutral-900">Additional Deceased Details</h3>
                  <p className="text-sm text-neutral-500">Age, IC, place and time of death</p>
                </div>
              </div>
              <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full font-medium">Optional</span>
            </button>
            {showDeceasedDetails && (
              <div className="px-6 py-4 border-t border-neutral-200 bg-gray-50">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="deceasedAge" className="block text-sm font-medium text-neutral-700 mb-2">
                      Age <span className="text-neutral-400 text-xs">(optional)</span>
                    </label>
                    <input
                      id="deceasedAge"
                      type="number"
                      min="0"
                      max="150"
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      {...register('deceasedAge', { valueAsNumber: true })}
                      placeholder="Age in years"
                    />
                  </div>

                  <div>
                    <label htmlFor="deceasedIC" className="block text-sm font-medium text-neutral-700 mb-2">
                      IC Number <span className="text-neutral-400 text-xs">(optional)</span>
                    </label>
                    <input
                      id="deceasedIC"
                      type="text"
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      {...register('deceasedIC')}
                      placeholder="XXXXXX-XX-XXXX"
                    />
                  </div>

                  <div>
                    <label htmlFor="placeOfDeath" className="block text-sm font-medium text-neutral-700 mb-2">
                      Place of Death <span className="text-neutral-400 text-xs">(optional)</span>
                    </label>
                    <input
                      id="placeOfDeath"
                      type="text"
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      {...register('placeOfDeath')}
                      placeholder="e.g., Hospital, Home"
                    />
                  </div>

                  <div>
                    <label htmlFor="timeOfDeath" className="block text-sm font-medium text-neutral-700 mb-2">
                      Time of Death <span className="text-neutral-400 text-xs">(optional)</span>
                    </label>
                    <input
                      id="timeOfDeath"
                      type="datetime-local"
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      {...register('timeOfDeath')}
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="causeOfDeath" className="block text-sm font-medium text-neutral-700 mb-2">
                      Cause of Death <span className="text-neutral-400 text-xs">(optional)</span>
                    </label>
                    <input
                      id="causeOfDeath"
                      type="text"
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      {...register('causeOfDeath')}
                      placeholder="e.g., Natural causes, illness"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Secondary Contact */}
          <div className="bg-white border border-neutral-200 rounded-lg shadow-sm">
            <button
              type="button"
              onClick={() => setShowSecondaryContact(!showSecondaryContact)}
              className="w-full px-6 py-4 text-left hover:bg-neutral-50 transition-colors rounded-lg flex items-center justify-between group"
            >
              <div className="flex items-center">
                {showSecondaryContact ? (
                  <ChevronDownIcon className="w-5 h-5 text-neutral-500 mr-3 group-hover:text-neutral-700 transition-colors" />
                ) : (
                  <ChevronRightIcon className="w-5 h-5 text-neutral-500 mr-3 group-hover:text-neutral-700 transition-colors" />
                )}
                <div>
                  <h3 className="text-base font-semibold text-neutral-900">Secondary Contact</h3>
                  <p className="text-sm text-neutral-500">Backup contact information</p>
                </div>
              </div>
              <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full font-medium">Optional</span>
            </button>
            {showSecondaryContact && (
              <div className="px-6 py-4 border-t border-neutral-200 bg-gray-50">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="secondaryContact" className="block text-sm font-medium text-neutral-700 mb-2">
                      Secondary Contact Name <span className="text-neutral-400 text-xs">(optional)</span>
                    </label>
                    <input
                      id="secondaryContact"
                      type="text"
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      {...register('secondaryContact')}
                      placeholder="Backup contact name"
                    />
                  </div>

                  <div>
                    <Controller
                      name="secondaryContactPhone"
                      control={control}
                      render={({ field, fieldState }) => (
                        <PhoneInput
                          label="Secondary Phone"
                          value={field.value ?? ''}
                          onChange={field.onChange}
                          error={fieldState.error?.message}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Additional Burial Details */}
          <div className="bg-white border border-neutral-200 rounded-lg shadow-sm">
            <button
              type="button"
              onClick={() => setShowBurialDetails(!showBurialDetails)}
              className="w-full px-6 py-4 text-left hover:bg-neutral-50 transition-colors rounded-lg flex items-center justify-between group"
            >
              <div className="flex items-center">
                {showBurialDetails ? (
                  <ChevronDownIcon className="w-5 h-5 text-neutral-500 mr-3 group-hover:text-neutral-700 transition-colors" />
                ) : (
                  <ChevronRightIcon className="w-5 h-5 text-neutral-500 mr-3 group-hover:text-neutral-700 transition-colors" />
                )}
                <div>
                  <h3 className="text-base font-semibold text-neutral-900">Additional Burial Details</h3>
                  <p className="text-sm text-neutral-500">Time, mosque, cemetery, pickup address</p>
                </div>
              </div>
              <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full font-medium">Optional</span>
            </button>
            {showBurialDetails && (
              <div className="px-6 py-4 border-t border-neutral-200 bg-gray-50">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="preferredBurialTime" className="block text-sm font-medium text-neutral-700 mb-2">
                      Preferred Burial Time <span className="text-neutral-400 text-xs">(optional)</span>
                    </label>
                    <input
                      id="preferredBurialTime"
                      type="time"
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      {...register('preferredBurialTime')}
                    />
                  </div>

                  <div>
                    <label htmlFor="mosquePreference" className="block text-sm font-medium text-neutral-700 mb-2">
                      Mosque Preference <span className="text-neutral-400 text-xs">(optional)</span>
                    </label>
                    <input
                      id="mosquePreference"
                      type="text"
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      {...register('mosquePreference')}
                      placeholder="e.g., Masjid Jamek"
                    />
                  </div>

                  <div>
                    <label htmlFor="burialCemetery" className="block text-sm font-medium text-neutral-700 mb-2">
                      Preferred Cemetery <span className="text-neutral-400 text-xs">(optional)</span>
                    </label>
                    <input
                      id="burialCemetery"
                      type="text"
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      {...register('burialCemetery')}
                      placeholder="e.g., Jalan Ampang Muslim Cemetery"
                    />
                  </div>

                  <div>
                    <label htmlFor="burialPlot" className="block text-sm font-medium text-neutral-700 mb-2">
                      Plot Number <span className="text-neutral-400 text-xs">(optional)</span>
                    </label>
                    <input
                      id="burialPlot"
                      type="text"
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      {...register('burialPlot')}
                      placeholder="Section A, Plot 123"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="pickupAddress" className="block text-sm font-medium text-neutral-700 mb-2">
                      Pickup Address <span className="text-neutral-400 text-xs">(optional)</span>
                    </label>
                    <input
                      id="pickupAddress"
                      type="text"
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      {...register('pickupAddress')}
                      placeholder="Address where ambulance should pick up the deceased"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="notes" className="block text-sm font-medium text-neutral-700 mb-2">
                      Special Requirements <span className="text-neutral-400 text-xs">(optional)</span>
                    </label>
                    <textarea
                      id="notes"
                      rows={3}
                      className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Any additional information or special requirements"
                      {...register('notes')}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.994 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 1.998-.453 3.83-1.319 5.377a1.2 1.2 0 01.653 1.57c-.757.367-1.633-.55-2.504-.55-3.179 0-5.515 1.213-6.2.562-.757-.384-1.39-.1-1.953.292-.415.217-.79.48-1.102.766-.058.388-.172.915-.4 1.57-.134.904-.298 1.908-.298 2.906 0 2.066.902 3.989 2.293 5.35a13.6 13.6 0 002.323 1.53c.384.264.76.499 1.098.697.476.26.934.388 1.318.388.452 0 .902-.048 1.317-.144.727-.184 1.401-.349 2.005-.349 1.022 0 1.965.356 2.758.988.8.639 1.286 1.447 1.286 2.525 0 .947-.348 1.746-.938 2.39a1.2 1.2 0 01.655 1.618c-.896.602-1.913.9-2.913.9-.765 0-1.461-.165-2.058-.446a1.2 1.2 0 01-.842.2c-.577 0-1.135.087-1.634.246A11.954 11.954 0 0018 10c0-.736-.193-1.413-.489-2.042a1.2 1.2 0 01-.705-1.425c.657-.45 1.409-.752 2.22-.876.398-.08.79.053-1.167.158a1.2 1.2 0 01-.663-.06c-.538.052-1.06.153-1.532.288a1.2 1.2 0 01-.664-.06c-.826.223-1.598.552-2.29.992z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">
                Privacy & Respect
              </h3>
              <div className="mt-2 text-sm text-green-700">
                <p>All information provided is handled with utmost confidentiality and respect. We understand this is a sensitive time and are committed to providing compassionate service throughout the funeral arrangement process.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={isSubmitting}
          >
            Previous
          </Button>
          <div className="flex-1"></div>
          <Button
            variant="islamic"
            type="submit"
            disabled={!isValid || isSubmitting}
            loading={isSubmitting}
            className="min-w-48"
          >
            {isSubmitting ? 'Processing...' : 'Continue to Provider Matching'}
          </Button>
        </div>
      </form>
    </div>
  )
}

export default DeceasedInformationStep